import { useCallback, useEffect, useState } from "react";
import { useViimeisin } from "./tuoreus";
import { naytaPvm } from "./pvm";
import { api, ApiError, type Kalusto, type Laippakiristys as Kiristys, type LaippakiristysMenetelma, type LaippakiristysTila } from "./api";
import { t, virheTeksti, type KaannosAvain } from "./kieli";
import { Kenttavirhe, useKenttavirheet } from "./Kenttavirhe";

/* ═══════════════════════════════════════════════════════════
   Laippakiristys (264, T636: Toni 25.10.2026 klo 12.55, 14.15, 17.30).

   Yhden liitoksen kiristykset ja puhelinlomake. Kiristys on tapahtuma: uusi ei korvaa edellistä. Lomake näyttää vain
   menetelmän arvot (momenttiavain → Nm, kiristin → bar ja valinnainen kN), joten väärää yksikköä ei voi kirjata — ja
   palvelin sekä kanta tarkistavat saman. Varoitukset (kalibrointi, pätevyys raukeamassa / rauennut) eivät estä,
   mutta ne näytetään heti tallennuksen jälkeen ja jokaisella rivillä, joten hyväksyjä näkee ne.

   Työkalu kalustosta, jos tunnus saa lukea kaluston (työnjohto); muuten tunnus ja kalibrointipäivä tekstinä.
   ═══════════════════════════════════════════════════════════ */

const MENETELMAT: LaippakiristysMenetelma[] = ["momenttiavain", "hydraulinen_momenttiavain", "kiristin", "kasi"];
const VAROITUS: Record<string, KaannosAvain> = {
  kalibrointi_vanhentunut: "lk.v.kalibrointi_vanhentunut",
  kalibrointi_ei_tiedossa: "lk.v.kalibrointi_ei_tiedossa",
  patevyys_raukeamassa: "lk.v.patevyys_raukeamassa",
  patevyys_rauennut: "lk.v.patevyys_rauennut",
};
const TILA: Record<LaippakiristysTila, KaannosAvain> = {
  kirjattu: "lk.tila.kirjattu", hyvaksytty: "lk.tila.hyvaksytty", palautettu: "lk.tila.palautettu", tuotu: "lk.tila.tuotu",
};
/** Toni 25.10.2026 klo 20.00: työnjohto valitsee kiristäjän (oletus itse); kenttätili kirjaa aina itsensä (kutsuja päättää). */
type Ehdokas = { id: number; nimi: string; lyhenne: string | null; voimassa_asti: string | null };
const luku = (s: string): number | null => { const v = s.replace(",", ".").trim(); return v === "" ? null : Number(v); };
export const varoitusTeksti = (v: string): string => (VAROITUS[v] ? t(VAROITUS[v]!) : v);

export default function Laippakiristys({
  liitosId, saaKiristaa, saaKasitella, toisenPuolesta = false, omaHenkilo = null, onMuutos,
}: {
  liitosId: number;
  saaKiristaa: boolean;
  saaKasitella: boolean;
  /** Lomakkeessa kenttä «Kiristäjä» (työnjohto). Kenttätilillä aina false. */
  toisenPuolesta?: boolean;
  /** Kirjaajan oma henkilö: kiristäjän oletus, jos hän on ehdokkaissa. */
  omaHenkilo?: number | null;
  onMuutos?: () => void;
}) {
  const [ehdokkaat, setEhdokkaat] = useState<Ehdokas[] | null>(null);
  const [kiristaja, setKiristaja] = useState("");
  const [rivit, setRivit] = useState<Kiristys[] | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [ilmoitus, setIlmoitus] = useState<string | null>(null);
  const [auki, setAuki] = useState(false);
  const [lahetys, setLahetys] = useState(false);
  const [kalusto, setKalusto] = useState<Kalusto[] | null>(null);
  const [lomake, setLomake] = useState({ menetelma: "momenttiavain" as LaippakiristysMenetelma, momentti: "", paine: "", pulttivoima: "",
    tiiviste: "", pultit_maara: "", pultit_koko: "", kalusto_id: "", tyokalu_teksti: "", tyokalu_kalibroitu: "", paiva: "", huomiot: "" });
  const [peruste, setPeruste] = useState<Record<number, string>>({});
  const kv = useKenttavirheet<`p${number}`>();   // T677 J: palautuksen peruste rivikohtaisesti

  const uusi = useViimeisin();
  const lataa = useCallback(async () => {
    const tuore = uusi();
    try {
      const r = await api.laippakiristykset(liitosId);
      if (!tuore()) return;
      setRivit(r);
    } catch (e) {
      if (!tuore()) return;
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : String(e));
      setRivit([]);
    }
  }, [liitosId, uusi]);
  useEffect(() => { void lataa(); }, [lataa]);

  async function avaa() {
    setAuki(true); setIlmoitus(null);
    if (kalusto === null) {
      // Kenttätili ei saa lukea kalustoa (yrityksen tietoa): silloin työkalu kirjataan tekstinä.
      try { setKalusto((await api.kalusto()).filter((k) => k.laji === "tyokalu")); } catch { setKalusto([]); }
    }
    if (toisenPuolesta && ehdokkaat === null) {
      try {
        const e = await api.laippaKiristajat();
        setEhdokkaat(e);
        // Oletus kirjaaja itse, jos hän kelpaa; muuten valitaan (tyhjä valinta ei lähde palvelimelle kirjaajan nimissä).
        setKiristaja(omaHenkilo != null && e.some((x) => x.id === omaHenkilo) ? String(omaHenkilo) : "");
      } catch (e) {
        setEhdokkaat([]);
        setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : String(e));
      }
    }
  }

  async function tallenna() {
    setLahetys(true); setVirhe(null); setIlmoitus(null);
    const m = lomake.menetelma;
    const momenttiavain = m === "momenttiavain" || m === "hydraulinen_momenttiavain";
    try {
      const r = await api.kirjaaLaippakiristys(liitosId, {
        menetelma: m,
        momentti_nm: momenttiavain ? luku(lomake.momentti) : null,
        paine_bar: m === "kiristin" ? luku(lomake.paine) : null,
        pulttivoima_kn: m === "kiristin" ? luku(lomake.pulttivoima) : null,
        tiiviste: lomake.tiiviste.trim() || null,
        pultit_maara: lomake.pultit_maara.trim() ? Math.round(Number(lomake.pultit_maara)) : null,
        pultit_koko: lomake.pultit_koko.trim() || null,
        kalusto_id: lomake.kalusto_id ? Number(lomake.kalusto_id) : null,
        tyokalu_teksti: !lomake.kalusto_id && lomake.tyokalu_teksti.trim() ? lomake.tyokalu_teksti.trim() : null,
        tyokalu_kalibroitu: !lomake.kalusto_id && lomake.tyokalu_kalibroitu ? lomake.tyokalu_kalibroitu : null,
        ...(lomake.paiva ? { paiva: lomake.paiva } : {}),
        huomiot: lomake.huomiot.trim() || null,
        ...(toisenPuolesta ? { kiristaja_id: kiristaja ? Number(kiristaja) : null } : {}),
      });
      setIlmoitus(t("lk.tallennettu") + (r.varoitukset.length ? " ⚠ " + r.varoitukset.map(varoitusTeksti).join(" · ") : ""));
      setAuki(false);
      setLomake((l) => ({ ...l, momentti: "", paine: "", pulttivoima: "", huomiot: "" }));
      await lataa(); onMuutos?.();
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : String(e));
    } finally { setLahetys(false); }
  }

  async function kasittele(k: Kiristys, teko: "hyvaksy" | "palauta") {
    setVirhe(null); kv.tyhjenna();
    // T677 J (Toni 27.10.2026 klo 09.12): palautus ilman perustetta → punainen ilmoitus perustekentän alle ja kohdistus kenttään
    // (ennen palvelimen 400 näkyi pienenä harmaana tekstinä taulukon yläpuolella).
    if (teko === "palauta" && !(peruste[k.id] ?? "").trim()) { kv.aseta(`p${k.id}`, t("lk.v.kirjoita_peruste")); return; }
    try {
      await api.kasitteleLaippakiristys(k.id, teko, teko === "palauta" ? (peruste[k.id] ?? "").trim() : undefined);
      await lataa(); onMuutos?.();
    } catch (e) {
      if (e instanceof ApiError && e.virhe === "peruste_puuttuu") { kv.aseta(`p${k.id}`, t("lk.v.kirjoita_peruste")); return; }
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : String(e));
    }
  }

  const aseta =(k: keyof typeof lomake) => (e: { target: { value: string } }) => setLomake((l) => ({ ...l, [k]: e.target.value }));
  const kentta = { display: "flex", flexDirection: "column" as const, gap: 4, minWidth: 0 };
  const syote = { minHeight: 44, fontSize: 16 };
  const m = lomake.menetelma;

  return (
    <div data-lk-paneeli={liitosId} style={{ padding: "8px 0" }}>
      {virhe && <p className="huom" role="alert" data-lk-virhe>{virhe}</p>}
      {ilmoitus && <p role="status" data-lk-ilmoitus>{ilmoitus}</p>}

      {rivit === null ? <p className="dim">…</p> : rivit.length === 0 ? (
        <p className="dim">{t("lk.ei_kiristyksia")}</p>
      ) : (
        /* 375 px: kiristyslista vierii omassa säiliössään eikä levennä sivua (Toni 26.10.2026 klo 04.15). */
        <div className="taulu-ikkuna" data-taulu-ikkuna="laippakiristykset">
        <table className="taulu" data-lk-lista>
          <thead><tr><th>{t("lk.paiva")}</th><th>{t("lk.menetelma")}</th><th>{t("lk.arvo")}</th><th>{t("lk.kiristaja")}</th><th>{t("lk.tila")}</th></tr></thead>
          <tbody>
            {rivit.map((k) => (
              <tr key={k.id} data-lk-rivi={k.id} data-lk-tila={k.tila}>
                <td>{naytaPvm(k.paiva)}</td>
                <td>{t(`lk.m.${k.menetelma}` as KaannosAvain)}</td>
                <td className="mono">
                  {k.momentti_nm != null ? `${k.momentti_nm} Nm` : k.paine_bar != null ? `${k.paine_bar} bar${k.pulttivoima_kn != null ? ` · ${k.pulttivoima_kn} kN` : ""}` : <span className="dim">–</span>}
                  {k.varoitukset.length > 0 && (
                    <div className="huom" data-lk-varoitus>⚠ {k.varoitukset.map(varoitusTeksti).join(" · ")}</div>
                  )}
                </td>
                <td>{k.kiristaja ?? <span className="dim">–</span>}</td>
                <td>
                  {t(TILA[k.tila])}
                  {k.tila === "palautettu" && k.palautuksen_peruste && <div className="dim">{k.palautuksen_peruste}</div>}
                  {saaKasitella && k.tila === "kirjattu" && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                      <button className="nappi" style={syote} data-lk-hyvaksy onClick={() => void kasittele(k, "hyvaksy")}>{t("lk.hyvaksy")}</button>
                      <div style={{ display: "flex", flexDirection: "column", flex: "1 1 160px", minWidth: 0 }}>
                        <input placeholder={t("lk.palautuksen_peruste")} style={syote} data-lk-peruste {...kv.kentta(`p${k.id}`)}
                               value={peruste[k.id] ?? ""} onChange={(e) => { setPeruste((p) => ({ ...p, [k.id]: e.target.value })); if (e.target.value.trim()) kv.poista(`p${k.id}`); }} />
                        <Kenttavirhe {...kv.virhe(`p${k.id}`)} />
                      </div>
                      <button className="nappi nappi--kevyt" style={syote} data-lk-palauta onClick={() => void kasittele(k, "palauta")}>{t("lk.palauta")}</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {saaKiristaa && !auki && (
        <button className="nappi" style={{ ...syote, marginTop: 8 }} data-lk-avaa onClick={() => void avaa()}>{t("lk.kirjaa")}</button>
      )}
      {!saaKiristaa && <p className="dim" style={{ marginTop: 6 }}>{t("lk.ei_oikeutta")}</p>}

      {saaKiristaa && auki && (
        <div data-lk-lomake style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginTop: 8 }}>
          {toisenPuolesta && (
            <label style={{ ...kentta, gridColumn: "1 / -1" }}>{t("lk.kiristaja")}
              <select style={syote} value={kiristaja} data-lk-kiristaja onChange={(e) => setKiristaja(e.target.value)}>
                <option value="">{t("lk.kiristaja_valitse")}</option>
                {(ehdokkaat ?? []).map((x) => (
                  <option key={x.id} value={x.id}>{x.nimi}{x.lyhenne ? ` (${x.lyhenne})` : ""}{x.voimassa_asti ? ` · ${naytaPvm(x.voimassa_asti)}` : ""}</option>
                ))}
              </select>
              {ehdokkaat !== null && ehdokkaat.length === 0 && <span className="dim" data-lk-ei-ehdokkaita>{t("lk.kiristaja_ei_ehdokkaita")}</span>}
            </label>
          )}
          <label style={kentta}>{t("lk.menetelma")}
            <select style={syote} value={m} data-lk-menetelma onChange={aseta("menetelma")}>
              {MENETELMAT.map((x) => <option key={x} value={x}>{t(`lk.m.${x}` as KaannosAvain)}</option>)}
            </select></label>
          {(m === "momenttiavain" || m === "hydraulinen_momenttiavain") && (
            <label style={kentta}>{t("lk.momentti")}
              <input style={syote} inputMode="decimal" data-lk-momentti value={lomake.momentti} onChange={aseta("momentti")} /></label>
          )}
          {m === "kiristin" && (<>
            <label style={kentta}>{t("lk.paine")}
              <input style={syote} inputMode="decimal" data-lk-paine value={lomake.paine} onChange={aseta("paine")} /></label>
            <label style={kentta}>{t("lk.pulttivoima")}
              <input style={syote} inputMode="decimal" data-lk-pulttivoima value={lomake.pulttivoima} onChange={aseta("pulttivoima")} /></label>
          </>)}
          <label style={kentta}>{t("lk.tiiviste")}<input style={syote} value={lomake.tiiviste} onChange={aseta("tiiviste")} /></label>
          <label style={kentta}>{t("lk.pultit_maara")}<input style={syote} inputMode="numeric" value={lomake.pultit_maara} onChange={aseta("pultit_maara")} /></label>
          <label style={kentta}>{t("lk.pultit_koko")}<input style={syote} value={lomake.pultit_koko} onChange={aseta("pultit_koko")} /></label>
          {kalusto && kalusto.length > 0 && (
            <label style={kentta}>{t("lk.tyokalu")}
              <select style={syote} value={lomake.kalusto_id} data-lk-kalusto onChange={aseta("kalusto_id")}>
                <option value="">{t("lk.tyokalu_ei")}</option>
                {kalusto.map((k) => <option key={k.id} value={k.id}>{k.tunnus} · {k.nimi}</option>)}
              </select></label>
          )}
          {!lomake.kalusto_id && (<>
            <label style={kentta}>{t("lk.tyokalu_teksti")}<input style={syote} data-lk-tyokalu value={lomake.tyokalu_teksti} onChange={aseta("tyokalu_teksti")} /></label>
            <label style={kentta}>{t("lk.tyokalu_kalibroitu")}<input style={syote} type="date" data-lk-kalibroitu value={lomake.tyokalu_kalibroitu} onChange={aseta("tyokalu_kalibroitu")} /></label>
          </>)}
          <label style={kentta}>{t("lk.paiva")}<input style={syote} type="date" value={lomake.paiva} onChange={aseta("paiva")} /></label>
          <label style={{ ...kentta, gridColumn: "1 / -1" }}>{t("lk.huomiot")}<input style={syote} value={lomake.huomiot} onChange={aseta("huomiot")} /></label>
          <div style={{ display: "flex", gap: 8, gridColumn: "1 / -1" }}>
            <button className="nappi" style={syote} data-lk-tallenna disabled={lahetys} onClick={() => void tallenna()}>
              {lahetys ? t("lk.tallennetaan") : t("lk.tallenna")}</button>
            <button className="nappi nappi--kevyt" style={syote} onClick={() => setAuki(false)}>{t("lk.peruuta")}</button>
          </div>
        </div>
      )}
      <p className="dim" style={{ marginTop: 6 }}>{t("lk.selite")}</p>
    </div>
  );
}
